// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Keyboard } from "@capacitor/keyboard";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Preferences } from "@capacitor/preferences";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@capacitor/status-bar";
import { SAFE_URL_SCHEMES, toSafeExternalUrl as toSafeExternalUrlPure } from "./safe-url";

export { SAFE_URL_SCHEMES };

const DEBUG_PREF_KEY = "pssmngr.webviewDebugging";

function getExternalBaseUrl(): string {
  const configured = import.meta.env.VITE_APP_URL?.trim();

  if (configured) {
    return configured;
  }

  if (
    typeof window !== "undefined" &&
    window.location.origin &&
    !window.location.origin.startsWith("capacitor://")
  ) {
    return window.location.origin;
  }

  return "https://pssmngr.com";
}

/**
 * Resolve `url` against the app base URL and return it only if it uses a safe
 * scheme. Returns `null` for unparseable or disallowed-scheme URLs.
 */
export function toSafeExternalUrl(url: string): string | null {
  return toSafeExternalUrlPure(url, getExternalBaseUrl());
}

/**
 * Back-compat wrapper. Throws on disallowed/unparseable URLs so existing
 * callers that expect a string keep type-checking; prefer `toSafeExternalUrl`
 * for new code.
 */
export function toExternalUrl(url: string): string {
  const safe = toSafeExternalUrl(url);
  if (safe === null) {
    throw new Error("Refusing to build external URL with unsafe scheme");
  }
  return safe;
}

export const native = {
  isNative: Capacitor.isNativePlatform(),
  platform: Capacitor.getPlatform(),
  app: App,
  browser: Browser,
  statusBar: StatusBar,
  splashScreen: SplashScreen,
  keyboard: Keyboard,
  preferences: Preferences,
  localNotifications: LocalNotifications,
  pushNotifications: PushNotifications,
};

export async function openExternalUrl(url: string): Promise<void> {
  const safe = toSafeExternalUrl(url);
  if (safe === null) {
    // Silently refuse javascript:/data:/file: and other unsafe schemes.
    return;
  }

  if (native.isNative) {
    await Browser.open({ url: safe });
    return;
  }

  window.open(safe, "_blank", "noopener,noreferrer");
}

export function canEnableWebViewDebugging(): boolean {
  return __CAP_ALLOW_WEB_DEBUG__;
}

export async function setWebViewDebuggingEnabled(enabled: boolean): Promise<boolean> {
  if (!canEnableWebViewDebugging()) {
    await Preferences.remove({ key: DEBUG_PREF_KEY });
    return false;
  }

  await Preferences.set({ key: DEBUG_PREF_KEY, value: String(enabled) });
  window.dispatchEvent(
    new CustomEvent("pssmngr:webview-debugging", { detail: { enabled } }),
  );
  return enabled;
}

export async function getWebViewDebuggingEnabled(): Promise<boolean> {
  if (!canEnableWebViewDebugging()) {
    return false;
  }

  const { value } = await Preferences.get({ key: DEBUG_PREF_KEY });
  return value === "true";
}
